import { Controller } from '@hotwired/stimulus'
import {
  calendlyPageHeightPx,
  isCalendlyMessageEvent,
  loadCalendlyWidgetScript,
  mountCalendlyInlineWidget,
} from '../utils/calendlyEmbed'
import displayFlash from '../utils/displayFlash'

const DEFAULT_HEIGHT_PX = 700

type OpenParams = {
  url: string
  name: string
  email: string
  organisation: string
  productSlug: string
  productName: string
}

function buildSchedulingUrl(baseUrl: string): string {
  try {
    const url = new URL(baseUrl)
    url.searchParams.set('hide_gdpr_banner', '1')
    return url.toString()
  } catch {
    const joiner = baseUrl.includes('?') ? '&' : '?'
    return `${baseUrl}${joiner}hide_gdpr_banner=1`
  }
}

export default class extends Controller {
  static targets = ['dialog', 'embed', 'title', 'openTab', 'status']

  static values = {
    railsEnv: { type: String, default: 'development' },
  }

  declare dialogTarget: HTMLDialogElement
  declare embedTarget: HTMLDivElement
  declare titleTarget: HTMLElement
  declare openTabTarget: HTMLAnchorElement
  declare hasStatusTarget: boolean
  declare statusTarget: HTMLElement
  declare railsEnvValue: string

  declare boundMessageHandler: (event: MessageEvent) => void

  connect() {
    this.boundMessageHandler = this.onMessage.bind(this)
    window.addEventListener('message', this.boundMessageHandler)
  }

  disconnect() {
    window.removeEventListener('message', this.boundMessageHandler)
    this.clearEmbed()
  }

  open(event: Event & { params: OpenParams }) {
    event.preventDefault()
    const { url, name, email, organisation, productSlug, productName } = event.params
    if (!url?.trim()) return

    const displayName = name?.trim() || 'coachee'
    const productLabel = productName?.trim() || productSlug?.trim() || 'session'
    this.titleTarget.textContent = `Book ${productLabel} for ${displayName}`

    if (this.hasStatusTarget) {
      this.statusTarget.textContent = ''
    }

    const schedulingUrl = buildSchedulingUrl(url.trim())
    this.openTabTarget.href = schedulingUrl

    this.embedTarget.style.height = `${DEFAULT_HEIGHT_PX}px`
    this.dialogTarget.showModal()

    loadCalendlyWidgetScript()
      .then(() => {
        if (!this.dialogTarget.open) return
        mountCalendlyInlineWidget(this.embedTarget, {
          url: schedulingUrl,
          prefill: {
            name: name?.trim() || undefined,
            email: email?.trim() || undefined,
            customAnswers: organisation?.trim()
              ? { a1: organisation.trim() }
              : undefined,
          },
          utm: {
            utmSource: `SanctusBookingSystem-${this.railsEnvValue}`,
            utmCampaign: productSlug?.trim() || undefined,
            utmMedium: 'CoachBackupBooking',
            utmContent: email?.trim() || undefined,
          },
          resize: true,
        })
      })
      .catch(() => {
        if (this.hasStatusTarget) {
          this.statusTarget.textContent =
            'Could not load Calendly. Try opening the link in a new tab.'
        }
      })
  }

  close(event?: Event) {
    event?.preventDefault()
    if (this.dialogTarget.open) {
      this.dialogTarget.close()
    }
    this.clearEmbed()
  }

  onDialogClose() {
    this.clearEmbed()
  }

  private clearEmbed() {
    this.embedTarget.innerHTML = ''
    this.embedTarget.style.height = `${DEFAULT_HEIGHT_PX}px`
  }

  private onMessage(event: MessageEvent) {
    if (!isCalendlyMessageEvent(event)) return

    const nextHeight = calendlyPageHeightPx(event)
    if (nextHeight != null && this.dialogTarget.open) {
      this.embedTarget.style.height = `${Math.max(nextHeight, 320)}px`
    }

    if (event.data.event === 'calendly.event_scheduled' && this.dialogTarget.open) {
      displayFlash('Session booked for the coachee.', 8000)
      if (this.hasStatusTarget) {
        this.statusTarget.textContent =
          'Booking confirmed. Receipts usually appear after a short delay.'
      }
    }
  }
}
